// Frontend/src/contexts/ContactContext.js
import { createContext, useContext, useState } from 'react';
import contactService from '../services/contactService';

const ContactContext = createContext();

export const useContact = () => {
  return useContext(ContactContext);
};

export const ContactProvider = ({ children }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendContactMessage = async (formData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const response = await contactService.submitContactForm(JSON.stringify(formData));
  
      const data = response;

      if (data.success) {
        setSubmitSuccess(true);
        return response;
      } else {
        setSubmitError(data.message || 'There was an error sending your message. Please try again.');
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error sending contact message:', error);
      setSubmitError('There was an error sending your message. Please try again.');
      return { success: false, error: 'Network error' };
    } finally {
      setIsSubmitting(false);
    }
  };

  const getContactMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contactService.getContactMessages();
      setMessages(response);
      console.log('Fetched contact messages:', response);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      setError(error.message || 'Failed to fetch contact messages');
      throw new Error('Failed to fetch contact messages');
    }
    finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await contactService.markMessageAsRead(messageId);
      // Update the message status in local state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, status: 'read' }
          : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await contactService.deleteMessage(messageId);
      // Remove the message from local state
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  };

  const resetForm = () => {
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  const value = {
    isSubmitting,
    submitSuccess,
    submitError,
    sendContactMessage,
    getContactMessages,
    selectedMessage,
    setSelectedMessage,
    resetForm,
    messages,
    loading,
    error,
    markAsRead,
    deleteMessage
  };
  
  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
};

export default ContactContext; 